import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { WizardPage, wizardProps } from '@xengage/gw-portals-wizard-react';
import _ from 'lodash';
import React, { useCallback, useContext, useMemo } from 'react';
import { CodegenContext } from '../../components/CodegenContext';
import {
    useAccordionStates,
    useCoverable,
    useCoverages,
    useExposures,
    useRefState,
} from '../../hooks';
import './CoverablesPage.messages';
import presentationMetadata from './CoverablesPage.metadata.json5';
import styles from './CoverablesStyles.module.scss';
import { BreakpointTrackerContext } from '@jutro/legacy/layout';

function SingleCoverablePage(props) {
    const { authHeader } = useAuthentication();
    const [isSaving, updateIsSaving, isSavingRef] = useRefState(false);
    const {
        wizardData: submissionVM,
        currentStep: { pageKey },
        updateWizardData,
    } = props;
    const { isComponentValid, initialValidation, onValidate } =
        useValidation('CoverablePage');
    const jobID = submissionVM.value.quoteID;
    const { sessionUUID } = submissionVM.value;

    const { lobName } = useContext(CodegenContext);
    const metadata = presentationMetadata[pageKey];

    const breakpoint = useContext(BreakpointTrackerContext);
    const {
        componentProps: { path, parentPath, coverableType },
    } = _.get(presentationMetadata, pageKey);

    const owningCoverablePublicID = _.get(
        submissionVM,
        `${parentPath}.publicID.value`
    );

    let coverableVM = _.get(submissionVM, path);
    if (!coverableVM) {
        const pathToCollection = path.substr(
            0,
            path.lastIndexOf('.children[0]')
        );
        _.set(submissionVM, `${pathToCollection}.value`, [{}]);
        coverableVM = _.get(submissionVM, path);
    }

    const { saveCoverable, handleFieldBlur } = useCoverable({
        coverableVM,
        coverableType,
        jobID,
        sessionUUID,
        lobName,
        owningCoverablePublicID,
        updateIsSaving,
        isSavingRef,
        authHeader,
    });
    useMemo(async () => {
        if (coverableVM && coverableVM.aspects.subtreeValid) {
            await saveCoverable();
        }
    }, []);

    const {
        updateCoverage,
        syncCoverages,
        updateAndSyncCoverages,
        loadingClause,
    } = useCoverages({
        lobName,
        ownerVM: submissionVM,
        coverableVM,
        coverableType,
        jobID: submissionVM.value.quoteID,
        sessionUUID: submissionVM.value.sessionUUID,
        updateIsSaving,
        clausesBasePath: `${path}.coverages`,
        authHeader,
    });

    const { addExposure, deleteExposure } = useExposures({
        ownerVM: submissionVM,
    });

    const { openAccordions, accordionCallbackProps, renderKey } =
        useAccordionStates({
            ownerVM: submissionVM,
        });

    const addNewExposure = useCallback(
        callbackProps => {
            addExposure(callbackProps);
            openAccordions(callbackProps);
        },
        [addExposure]
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
            showOptional: true,
            onBlur: handleFieldBlur,
        },
        coverages: {
            visible: isComponentValid,
            loadingClause,
        },
        conditions: {
            visible: isComponentValid,
            loadingClause,
        },
        exclusions: {
            visible: isComponentValid,
            loadingClause,
        },
        infoBox: {
            visible: !isComponentValid,
        },
        ...accordionCallbackProps(),
    };

    const onNext = useCallback(async () => {
        await saveCoverable();
        return submissionVM;
    }, [saveCoverable, submissionVM]);

    return (
        <WizardPage
            skipWhen={() => initialValidation}
            disableNext={!isComponentValid || isSaving}
            disablePrevious={!isComponentValid}
            onNext={onNext}
        >
            <div key={renderKey()}>
                <ViewModelForm
                    uiProps={metadata.content}
                    model={submissionVM}
                    overrideProps={overrideProps}
                    onModelChange={updateWizardData}
                    callbackMap={{
                        onChangeClause: updateCoverage,
                        onSyncCoverages: syncCoverages,
                        onChangeSubmissionAndSync: updateAndSyncCoverages,
                        addExposure: addNewExposure,
                        deleteExposure,
                        onValidate: onValidate,
                    }}
                    onValidationChange={onValidate}
                    classNameMap={styles}
                />
            </div>
        </WizardPage>
    );
}

SingleCoverablePage.propTypes = wizardProps;
export default SingleCoverablePage;
